import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { isAiBudgetExceeded } from './ai-budget-guard';

export class ApiFetchError extends Error {
    constructor(
        message: string,
        public status: number,
        public body?: unknown,
    ) {
        super(message);
        this.name = 'ApiFetchError';
    }
}

interface ApiFetchOptions extends RequestInit {
    silent?: boolean;
    errorMessage?: string;
}

let lastErrorToastTime = 0;

function getCsrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
}

function getXsrfCookieToken(): string {
    if (typeof document === 'undefined' || !document.cookie) return '';
    const match = document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='));
    if (!match) return '';
    try {
        return decodeURIComponent(match.split('=')[1] || '');
    } catch {
        return '';
    }
}

export async function apiFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
    const { silent = false, errorMessage, ...fetchOptions } = options;

    // Merge in default headers
    const headers = new Headers(fetchOptions.headers);
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    if (!headers.has('Content-Type') && fetchOptions.method && fetchOptions.method !== 'GET' && !isFormData) {
        headers.set('Content-Type', 'application/json');
    }
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (!headers.has('X-CSRF-TOKEN')) {
        headers.set('X-CSRF-TOKEN', getCsrfToken());
    }
    // Also send X-XSRF-TOKEN from the cookie. Laravel rotates the cookie on every
    // response, so this stays fresh even when the <meta> CSRF token is stale
    // (e.g. after impersonation start/stop in another tab). Laravel's VerifyCsrfToken
    // accepts either header, so this is additive.
    if (!headers.has('X-XSRF-TOKEN')) {
        const xsrf = getXsrfCookieToken();
        if (xsrf) headers.set('X-XSRF-TOKEN', xsrf);
    }

    let response: Response;
    try {
        response = await fetch(url, { ...fetchOptions, headers });
    } catch (err) {
        // Network error (offline, DNS failure, etc.)
        if (!silent) {
            const now = Date.now();
            if (now - lastErrorToastTime > 10_000) {
                lastErrorToastTime = now;
                toast.error('Network error — check your connection');
            }
        }
        Sentry.captureException(err, { tags: { source: 'apiFetch', url } });
        throw new ApiFetchError('Network error', 0);
    }

    // Check for AI budget exceeded (429)
    if (await isAiBudgetExceeded(response)) {
        throw new ApiFetchError('AI budget exceeded', 429);
    }

    if (!response.ok) {
        let body: unknown;
        try {
            body = await response.clone().json();
        } catch {
            // Response wasn't JSON
        }

        if (!silent) {
            const now = Date.now();
            if (now - lastErrorToastTime > 5_000) {
                lastErrorToastTime = now;
                const msg = errorMessage || (response.status === 422 ? 'Validation error' : 'Something went wrong');
                toast.error(msg);
            }
        }

        const error = new ApiFetchError(`HTTP ${response.status}`, response.status, body);
        Sentry.captureException(error, {
            tags: { source: 'apiFetch', url, status: String(response.status) },
        });
        throw error;
    }

    return response;
}

/**
 * Convenience wrapper: apiFetch + parsed JSON response typed as T.
 * Use this when you already know the response shape — saves the caller
 * from writing `(await apiFetch(...)).json() as Promise<T>` everywhere.
 */
export async function apiJson<T>(url: string, options: ApiFetchOptions = {}): Promise<T> {
    const res = await apiFetch(url, options);
    if (res.status === 204) {
        // 204 No Content — return an empty object cast as T for callers that
        // don't expect a body (e.g., DELETE endpoints).
        return {} as T;
    }
    return (await res.json()) as T;
}
